<template>
  <el-dialog
    title="新建测试"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
    @open="handleOpen"
  >
    <el-form
      ref="createTestForm"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item label="测试名称" prop="testName">
        <el-input
          v-model="formData.testName"
          placeholder="请输入测试名称"
          maxlength="50"
        />
      </el-form-item>

      <el-form-item label="测试类型" prop="testType">
        <el-select
          v-model="formData.testType"
          placeholder="请选择测试类型"
          style="width: 100%"
        >
          <el-option label="日常测试" value="daily" />
          <el-option label="周测" value="weekly" />
          <el-option label="月测" value="monthly" />
          <el-option label="期末测试" value="final" />
        </el-select>
      </el-form-item>

      <el-form-item label="题目数量" prop="questionCount">
        <el-input-number
          v-model="formData.questionCount"
          :min="10"
          :max="200"
          :step="10"
          controls-position="right"
          style="width: 100%"
        />
        <span style="margin-left: 10px; color: #909399">题</span>
      </el-form-item>

      <el-form-item label="测试时长" prop="duration">
        <el-input-number
          v-model="formData.duration"
          :min="10"
          :max="180"
          :step="5"
          controls-position="right"
          style="width: 100%"
        />
        <span style="margin-left: 10px; color: #909399">分钟</span>
      </el-form-item>

      <el-form-item label="测试范围" prop="scope">
        <el-select
          v-model="formData.scope"
          placeholder="请选择测试范围"
          style="width: 100%"
        >
          <el-option label="全部单词" value="all" />
          <el-option label="最近学习" value="recent" />
          <el-option label="掌握较差" value="weak" />
          <el-option label="指定课程" value="course" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="formData.scope === 'course'" label="选择课程" prop="courseId">
        <el-select
          v-model="formData.courseId"
          placeholder="请选择课程"
          style="width: 100%"
        >
          <el-option
            v-for="course in studentCourses"
            :key="course.courseId"
            :label="course.courseName"
            :value="course.courseId"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="测试日期" prop="testDate">
        <el-date-picker
          v-model="formData.testDate"
          type="datetime"
          placeholder="选择日期时间"
          format="yyyy-MM-dd HH:mm"
          value-format="yyyy-MM-dd HH:mm"
          style="width: 100%"
        />
      </el-form-item>

      <el-form-item label="备注">
        <el-input
          v-model="formData.remark"
          type="textarea"
          :rows="2"
          placeholder="请输入备注"
        />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">
        创建测试
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'CreateTestDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    studentId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      submitting: false,
      studentCourses: [],
      formData: {
        testName: '',
        testType: 'daily',
        questionCount: 50,
        duration: 30,
        scope: 'all',
        courseId: null,
        testDate: '',
        remark: '',
      },
      formRules: {
        testName: [{ required: true, message: '请输入测试名称', trigger: 'blur' }],
        testType: [{ required: true, message: '请选择测试类型', trigger: 'change' }],
        questionCount: [{ required: true, message: '请输入题目数量', trigger: 'blur' }],
        duration: [{ required: true, message: '请输入测试时长', trigger: 'blur' }],
        scope: [{ required: true, message: '请选择测试范围', trigger: 'change' }],
        testDate: [{ required: true, message: '请选择测试日期', trigger: 'change' }],
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
  },
  methods: {
    ...mapActions('student', ['fetchStudentCourses']),

    /**
     * 对话框打开时
     */
    async handleOpen() {
      this.resetForm();
      this.formData.testDate = new Date().toISOString().slice(0, 16).replace('T', ' ');
      await this.loadStudentCourses();
    },

    /**
     * 加载学员课程
     */
    async loadStudentCourses() {
      try {
        this.studentCourses = await this.fetchStudentCourses(this.studentId);
      } catch (error) {
        console.error('加载学员课程失败:', error);
      }
    },

    /**
     * 关闭对话框
     */
    handleClose() {
      this.dialogVisible = false;
      this.resetForm();
    },

    /**
     * 重置表单
     */
    resetForm() {
      this.formData = {
        testName: '',
        testType: 'daily',
        questionCount: 50,
        duration: 30,
        scope: 'all',
        courseId: null,
        testDate: '',
        remark: '',
      };
      this.$nextTick(() => {
        if (this.$refs.createTestForm) {
          this.$refs.createTestForm.clearValidate();
        }
      });
    },

    /**
     * 提交表单
     */
    handleSubmit() {
      this.$refs.createTestForm.validate(async (valid) => {
        if (!valid) {
          return false;
        }

        this.submitting = true;
        try {
          // 这里应该调用创建测试API
          await new Promise((resolve) => setTimeout(resolve, 1000));
          this.$message.success('创建测试成功');
          this.$emit('success');
          this.handleClose();
        } catch (error) {
          console.error('创建测试失败:', error);
          this.$message.error('创建测试失败');
        } finally {
          this.submitting = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}
</style>
